import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Mail, Lock, CheckCircle } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  // Request reset state
  const [email, setEmail] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  
  // Reset password state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsRequesting(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/request-password-reset`, { email });
      setRequestSent(true);
      toast.success("E-Mail wurde gesendet");
    } catch (error) {
      // Don't reveal if email exists or not for security
      setRequestSent(true);
      toast.success("Falls ein Konto mit dieser E-Mail existiert, erhalten Sie eine E-Mail.");
    } finally {
      setIsRequesting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    
    setIsResetting(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success("Passwort wurde erfolgreich geändert");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Zurücksetzen des Passworts";
      toast.error(message);
    } finally {
      setIsResetting(false);
    }
  };

  // If we have a token, show the reset form
  if (token) {
    if (resetComplete) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-0 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-manrope font-bold text-xl text-slate-900 mb-2">
                Passwort geändert
              </h2>
              <p className="text-slate-500 mb-6">
                Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt anmelden.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                data-testid="go-to-login-btn"
              >
                Zur Anmeldung
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="mb-4">
              <img src={LOGO_URL} alt="plutec" className="h-12 mx-auto" />
            </div>
            <CardTitle className="font-manrope text-2xl text-[#1e3a5f]">
              Neues Passwort festlegen
            </CardTitle>
            <CardDescription>
              Geben Sie Ihr neues Passwort ein
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-slate-700">
                  Neues Passwort
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                    className="pl-10"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">
                  Passwort bestätigen
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Passwort wiederholen"
                    className="pl-10"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                disabled={isResetting}
                data-testid="reset-password-btn"
              >
                {isResetting ? "Wird gespeichert..." : "Passwort speichern"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No token - show request form
  if (requestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="font-manrope font-bold text-xl text-slate-900 mb-2">
              E-Mail gesendet
            </h2>
            <p className="text-slate-500 mb-6">
              Falls ein Konto mit dieser E-Mail existiert, haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Bitte überprüfen Sie auch Ihren Spam-Ordner.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Anmeldung
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mb-4">
            <img src={LOGO_URL} alt="plutec" className="h-12 mx-auto" />
          </div>
          <CardTitle className="font-manrope text-2xl text-[#1e3a5f]">
            Passwort vergessen?
          </CardTitle>
          <CardDescription>
            Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                E-Mail-Adresse
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="pl-10"
                  required
                  data-testid="reset-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
              disabled={isRequesting}
              data-testid="request-reset-btn"
            >
              {isRequesting ? "Wird gesendet..." : "Link senden"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-[#1e3a5f] hover:underline inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Zurück zur Anmeldung
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
